import { useEffect, useState } from 'react'
import { apiClient } from '@/analysis/apiClient'
import type { ActiveFilters } from '@/types'
import { DEFAULT_SLICE, type Query } from '@/analysis/apiTypes'
import { toFilterQuery } from '@/analysis/queryBuilder'
import { toApiDimensionType } from '@/analysis/dimensionApiMap'
import type { SearchSliceNode } from '@/analysis/searchContract'
import type { SeriesRow, Selection, TimeWindow } from '@/views/deepDiveView/types'
import { stableStringify, toApiWindow, windowLengthMs } from '@/views/deepDiveView/TimeWindow'
import { findMatchingNode } from '@/views/deepDiveView/selection'
import type { Dimension } from '@/types'

/**
 * How many multiples of the visible window we keep in memory for the rewind
 * track. 3× lets an operator scrub back through ~3 windows of history
 * without re-fetching, without pinning unbounded memory.
 */
const BUFFER_MULTIPLIER = 3

/**
 * Hard cap on rows held in memory regardless of window length. Acts as a
 * safety belt against very high tick rates (e.g. multi-Hz WS updates over
 * the 'all' window.
 */
const MAX_BUFFERED_ROWS = 20_000

type Args = {
  selected: Selection | null
  timeMode: 'live' | 'historic'
  timeWindow: TimeWindow
  historicTs: number | null | undefined
  historicFromTs?: number | null | undefined
  filters: ActiveFilters
  /**
   * Live search payload from the websocket — when present, contains
   * current-tick slice nodes for the subscribed scope.
   */
  searchNodes: ReadonlyArray<SearchSliceNode> | null | undefined
}

const buildTimeArg = (
  timeMode: 'live' | 'historic',
  timeWindow: TimeWindow,
  historicTs: number | null | undefined,
  historicFromTs?: number | null | undefined,
) => {
  if (timeMode === 'historic' && timeWindow === 'custom' && historicFromTs != null && historicTs != null) {
    return {
      mode: 'historic' as const,
      window: 'custom' as const,
      startTs: Math.min(historicFromTs, historicTs),
      endTs: Math.max(historicFromTs, historicTs),
    }
  }
  const window = toApiWindow(timeWindow)
  return timeMode === 'historic'
    ? { mode: 'historic' as const, window, startTs: historicTs ?? undefined, endTs: historicTs ?? undefined }
    : { mode: 'live' as const, window }
}

/** Build the analysis query from a deep-dive selection + UI filter state. */
const buildAnalysisQuery = (
  selected: Selection,
  filters: ActiveFilters,
  time: ReturnType<typeof buildTimeArg>,
): Query => {
  const isStream = selected.dimension === 'stream'
  return {
    view: 'analysis',
    dimension: toApiDimensionType((isStream ? 'continent' : selected.dimension) as Dimension),
    slice: isStream
      ? { type: selected.sliceType ?? 'global', value: selected.sliceValue ?? '*' }
      : { type: selected.dimension, value: selected.value },
    time,
    filters: toFilterQuery(filters, { streamId: isStream ? selected.value : null }),
    limit: 5000,
    offset: 0,
  }
}

void DEFAULT_SLICE // referenced for symmetry with other call sites

function pickMetric(m: Record<string, { displayValue?: number | null; mean?: number | null; min?: number | null; max?: number | null; p50?: number | null; p95?: number | null; tier?: number | null }> | undefined, key: string, field: 'displayValue' | 'mean' | 'min' | 'max' | 'p50' | 'p95') {
  const v = m?.[key]?.[field]
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function pickTier(m: Record<string, { tier?: number | null }> | undefined, key: string): 0 | 1 | 2 | null {
  const t = m?.[key]?.tier
  return t === 0 || t === 1 || t === 2 ? t : null
}

export const seriesRowFromNode = (node: SearchSliceNode): SeriesRow => {
  const m = node.metrics as Record<string, { displayValue?: number | null; mean?: number | null; min?: number | null; max?: number | null; p50?: number | null; p95?: number | null; tier?: number | null }> | undefined
  const tsMs = Number.isFinite(node.windowEndMs)
    ? node.windowEndMs
    : Number.isFinite(node.windowStartMs)
      ? node.windowStartMs
      : Date.now()
  return {
    tsMs,
    demuxInRate: pickMetric(m, 'demuxInRate', 'displayValue'),
    demuxInRate_tier: pickTier(m, 'demuxInRate'),
    demuxInRate_mean: pickMetric(m, 'demuxInRate', 'mean'),
    demuxInRate_min: pickMetric(m, 'demuxInRate', 'min'),
    demuxInRate_max: pickMetric(m, 'demuxInRate', 'max'),
    demuxToDecodeLagMs: pickMetric(m, 'demuxToDecodeLag', 'displayValue'),
    demuxToDecodeLagMs_tier: pickTier(m, 'demuxToDecodeLag'),
    demuxToDecodeLagMs_mean: pickMetric(m, 'demuxToDecodeLag', 'mean'),
    demuxToDecodeLagMs_p95: pickMetric(m, 'demuxToDecodeLag', 'p95'),
    renderRatio: pickMetric(m, 'renderRatio', 'displayValue'),
    renderRatio_tier: pickTier(m, 'renderRatio'),
    downloadKbps: pickMetric(m, 'downloadKbps', 'displayValue'),
    downloadKbps_tier: pickTier(m, 'downloadKbps'),
    downloadKbps_mean: pickMetric(m, 'downloadKbps', 'mean'),
    downloadKbps_p50: pickMetric(m, 'downloadKbps', 'p50'),
    // E2E = mediaLatency only — never invent from other latency fields.
    e2eLatency: pickMetric(m, 'mediaLatency', 'displayValue'),
    e2eLatency_tier: pickTier(m, 'mediaLatency'),
    e2eLatency_mean: pickMetric(m, 'mediaLatency', 'mean'),
    e2eLatency_p95: pickMetric(m, 'mediaLatency', 'p95'),
  }
}

/**
 * Compute the buffer size (in ms) we want to keep behind the live edge.
 * `Infinity` for the 'all' window — the row-count cap is the only limiter.
 */
const bufferLengthMs = (timeWindow: TimeWindow): number => {
  const len = windowLengthMs(toApiWindow(timeWindow))
  return len === Infinity ? Infinity : len * BUFFER_MULTIPLIER
}

const trimBuffer = (rows: ReadonlyArray<SeriesRow>, bufferMs: number): SeriesRow[] => {
  const arr = rows.slice()
  if (bufferMs !== Infinity && arr.length) {
    const newest = arr[arr.length - 1]!.tsMs
    const cutoff = newest - bufferMs
    let drop = 0
    while (drop < arr.length && arr[drop]!.tsMs < cutoff) drop++
    if (drop > 0) arr.splice(0, drop)
  }
  if (arr.length > MAX_BUFFERED_ROWS) {
    arr.splice(0, arr.length - MAX_BUFFERED_ROWS)
  }
  return arr
}

/**
 * Owns the timeseries state for the deep-dive chart.
 *
 *  - Initial fetch (and refetch on selection/time/filters change) hits
 *    `POST /api/analysis` to seed the chart with historical context.
 *  - Live updates merge the matching slice from `searchNodes` (WS-driven
 *    dimension/stream snapshot) into the buffer.
 */
export function useSeriesData({
  selected,
  timeMode,
  timeWindow,
  historicTs,
  historicFromTs,
  filters,
  searchNodes,
}: Args) {
  const [series, setSeries] = useState<SeriesRow[] | null>(null)
  const [error, setError] = useState<unknown>(null)

  const filtersKey = stableStringify(filters)

  useEffect(() => {
    if (!selected) {
      setSeries(null)
      setError(null)
      return
    }
    let cancelled = false
    const time = buildTimeArg(timeMode, timeWindow, historicTs, historicFromTs)

    void (async () => {
      try {
        const query = buildAnalysisQuery(selected, filters, time)
        const res = await apiClient.analysisSearch(query)
        if (cancelled) return
        const rows = Array.isArray(res.nodes) ? res.nodes.map(seriesRowFromNode) : []
        setSeries(trimBuffer(rows, bufferLengthMs(timeWindow)))
        setError(null)
      } catch (e) {
        if (cancelled) return
        setSeries([])
        setError(e)
      }
    })()
    return () => { cancelled = true }
  }, [selected?.dimension, selected?.value, selected?.sliceType, selected?.sliceValue, timeMode, timeWindow, historicTs, historicFromTs, filtersKey])

  useEffect(() => {
    if (!selected || !searchNodes) return
    if (timeMode !== 'live') return
    const node = findMatchingNode(
      searchNodes,
      selected.value,
      selected.sliceType,
      selected.sliceValue,
    )
    if (!node) return

    const row = seriesRowFromNode(node)
    const bufferMs = bufferLengthMs(timeWindow)

    setSeries((prev) => {
      const cur = prev ?? []
      // Upsert by tsMs: same window updates in place; a newer tick appends.
      const without = cur.filter((r) => r.tsMs !== row.tsMs)
      return trimBuffer([...without, row].sort((a, b) => a.tsMs - b.tsMs), bufferMs)
    })
  }, [selected, searchNodes, timeMode, timeWindow])

  return { series, error }
}
